import React from 'react';

/**
 * Generic render error boundary.
 *
 * A single bad field in a deeply-nested results render used to throw during
 * render and unmount the whole React tree, leaving the user staring at a blank
 * white page. This boundary catches any render-time exception below it and
 * shows a friendly fallback (plus an optional raw-data dump) so the rest of the
 * page -- and at minimum the underlying data -- always stays visible.
 *
 * Props:
 *   - title:   heading shown in the fallback (default: "Something went wrong")
 *   - fallbackData: any JSON-serializable value to render in a <pre> so the
 *                   user can still see the result even if the rich UI failed.
 *   - children: the subtree to guard.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the console for debugging; never re-throw.
    // eslint-disable-next-line no-console
    console.error('DenialCase render error:', error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { title, fallbackData } = this.props;
    let raw = null;
    if (fallbackData != null) {
      try {
        raw = JSON.stringify(fallbackData, null, 2);
      } catch {
        raw = String(fallbackData);
      }
    }

    return (
      <div className="denials-alert denials-alert--error" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {title || 'Something went wrong rendering this section'}
        </div>
        <p style={{ margin: '0 0 8px', fontSize: 13 }}>
          The engine produced a result, but this part of the page could not be
          displayed. The raw data is shown below so nothing is lost.
        </p>
        {this.state.error?.message && (
          <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af' }}>
            {String(this.state.error.message)}
          </p>
        )}
        {raw && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 13 }}>Show raw result</summary>
            <pre className="denials-output-pre" style={{ marginTop: 8 }}>{raw}</pre>
          </details>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
